// LocalStoreInventory.tsx
import React, { useEffect, useState } from 'react';
import './LocalStoreInventory.scss';
import {
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Switch,
  Tabs,
  Tab,
  Grid
} from '@material-ui/core';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import AddIcon from '@material-ui/icons/Add';
import UploadIcon from '@material-ui/icons/CloudUpload';
import SearchIcon from '@material-ui/icons/Search';
import WidgetsIcon from '@material-ui/icons/Widgets';
import LocalMallIcon from '@material-ui/icons/LocalMall';
import ImageIcon from '@material-ui/icons/Image';
import CloseIcon from '@material-ui/icons/Close';
import StorefrontIcon from '@material-ui/icons/Storefront';
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import DirectionsWalkIcon from '@material-ui/icons/DirectionsWalk';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import CollectionsIcon from '@material-ui/icons/Collections';
import EditIcon from '@material-ui/icons/Edit';
import { http, general, AlertService, useNavigationService, localstorage } from '../fx-core';
import StoreOutlinedIcon from '@material-ui/icons/StoreOutlined';

interface RackOption {
  id: number;
  label: string;
  locationNote?: string;
}

interface ProductOption {
  id: number;
  rackId: number | null;
  rackLabel: string | null;
  source: string;
  name: string;
  quantity: number;
  price: number | null;
  description: string | null;
  photos: string[];
}

interface PresignSlot {
  uploadUrl: string;
  publicUrl: string;
  key: string;
}

interface LogoOption {
  id: number;
  imageUrl: string;
}

interface StoreDetails {
  storeName: string | null;
  logoId: number | null;
  logoUrl: string | null;
  activationStatus: string; // "Inactive" | "Active" | "Closed"
  autoActivate: boolean;
}

interface MainStoreProduct {
  stallItemId: number;
  stallId: number;
  stallName: string | null;
  name: string;
  description: string | null;
  quantity: number;
  price: number | null;
  imagePath: string | null;
  alreadyImported: boolean;
}

// Number of tabs on this screen: Store Details, Inventory, Racks.
const TAB_COUNT = 3;
const HOLD_TIME_OPTIONS = [5, 10, 15, 30];

const LocalStoreInventory = (props: any) => {
  const { back } = useNavigationService();
  const bookingId = props.match?.params?.bookingId ? parseInt(props.match.params.bookingId) : (props.bookingId || 0);
  const userId = localstorage.getItem("userId") ? parseInt(localstorage.getItem("userId")) : 0;

  // 0 = Store Details (incl. logo), 1 = Inventory, 2 = Racks
  const [activeTab, setActiveTab] = useState(0);
  const [storeDetailsError, setStoreDetailsError] = useState<string | null>(null);
  const [updateSuccessDialogOpen, setUpdateSuccessDialogOpen] = useState(false);

  // Validates that the merchant has completed Store Details before
  // allowing navigation to the Inventory or Racks tabs.
  const validateStoreDetails = (): boolean => {
    const errors: string[] = [];
    if (!storeDetails.logoUrl && !storeDetails.logoId) {
      errors.push('select or upload a logo for your stall');
    }
    if (!storeDetails.storeName || !storeDetails.storeName.trim()) {
      errors.push('name your stall');
    }
    if (cooldownMinutes === null || !HOLD_TIME_OPTIONS.includes(cooldownMinutes)) {
      errors.push('select a reservation hold time');
    }
    if (savingCooldown) {
      errors.push('wait for the reservation hold time to finish saving');
    }
    if (errors.length > 0) {
      setStoreDetailsError('Please ' + errors.join(', ') + ' before proceeding.');
      return false;
    }
    setStoreDetailsError(null);
    return true;
  };

  const handleTabChange = (_e: React.ChangeEvent<{}>, v: number) => {
    if (v > 0 && !validateStoreDetails()) return;
    setStoreDetailsError(null);
    setActiveTab(v);
  };

  // Previous/Next controls so merchants can step through the tabs
  // without having to reach for them directly every time.
  const goToPreviousTab = () => {
    setStoreDetailsError(null);
    setActiveTab((t) => Math.max(0, t - 1));
  };
  const goToNextTab = () => {
    if (activeTab === TAB_COUNT - 1) {
      setUpdateSuccessDialogOpen(true);
      return;
    }
    const nextTab = Math.min(TAB_COUNT - 1, activeTab + 1);
    if (nextTab > 0 && !validateStoreDetails()) return;
    setStoreDetailsError(null);
    setActiveTab(nextTab);
  };

  const [racks, setRacks] = useState<RackOption[]>([]);
  const [allProducts, setAllProducts] = useState<ProductOption[]>([]); // racked + unassigned
  const [inventory, setInventory] = useState<ProductOption[]>([]); // unassigned only
  const [loading, setLoading] = useState(true);

  const [isPickupEnabled, setIsPickupEnabled] = useState(true);
  const [isDeliveryEnabled, setIsDeliveryEnabled] = useState(false);

  // ── Reservation cooldown (how long a held order keeps its stock reserved) ──
  const [cooldownMinutes, setCooldownMinutes] = useState<number | null>(null);
  const [savingCooldown, setSavingCooldown] = useState(false);

  // ── Store Details (name + logo) ────────────────────────────────────
  const [storeDetails, setStoreDetails] = useState<StoreDetails>({ storeName: '', logoId: null, logoUrl: null, activationStatus: 'Inactive', autoActivate: false });
  const [savingAutoActivate, setSavingAutoActivate] = useState(false);
  const [storeNameInput, setStoreNameInput] = useState('');
  const [savingStoreName, setSavingStoreName] = useState(false);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);

  const isLocked = storeDetails.activationStatus === 'Active';

  // ── Logo library (merchant-level, reusable) ─────────────────────────
  const [logos, setLogos] = useState<LogoOption[]>([]);
  const [loadingLogos, setLoadingLogos] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Add/Edit Product dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [productName, setProductName] = useState('');
  const [productQuantity, setProductQuantity] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [newPhotoFiles, setNewPhotoFiles] = useState<File[]>([]);
  const [savingProduct, setSavingProduct] = useState(false);

  // Big "Add Product to Rack" modal
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignSearchText, setAssignSearchText] = useState('');
  const [assignSort, setAssignSort] = useState<'none' | 'newest'>('none');
  const [assignResults, setAssignResults] = useState<ProductOption[]>([]);
  const [loadingAssignResults, setLoadingAssignResults] = useState(false);
  const [assignTargetRackId, setAssignTargetRackId] = useState<number>(0);
  const [assignTargetRackLabel, setAssignTargetRackLabel] = useState<string>('');

  // Import from main store — browse + bulk import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [loadingMainStoreProducts, setLoadingMainStoreProducts] = useState(false);
  // Fetched once on page load (not just when the dialog opens) purely so the
  // "Import from my main store" button can be hidden entirely for merchants
  // who don't run any main store — never shown as a dead end.
  const [mainStoreStalls, setMainStoreStalls] = useState<{ id: number; name: string }[]>([]);
  const [mainStoreProducts, setMainStoreProducts] = useState<MainStoreProduct[]>([]);
  const [selectedStallIds, setSelectedStallIds] = useState<number[]>([]);
  const [checkedStallItemIds, setCheckedStallItemIds] = useState<Set<number>>(new Set());
  const [importingProducts, setImportingProducts] = useState(false);

  const getRacks = () => {
    return http.get({ url: `localstore/${bookingId}/racks` })
      .then((res: any) => setRacks(res.data?.racks || []))
      .catch(() => setRacks([]));
  };

  const getAllProducts = () => {
    return http.get({ url: `localstore/${bookingId}/products` })
      .then((res: any) => setAllProducts(res.data?.products || []))
      .catch(() => setAllProducts([]));
  };

  const getInventory = () => {
    return http.get({ url: `localstore/${bookingId}/inventory` })
      .then((res: any) => setInventory(res.data?.products || []))
      .catch(() => setInventory([]));
  };

  // Store name/logo/activation status come from the merchant's own "My Stalls"
  // list (already has everything we need) rather than a dedicated endpoint —
  // filtering by this bookingId keeps this screen to a single extra call.
  const getStoreDetails = () => {
    if (!userId) return Promise.resolve();
    return http.get({ url: `localstore/user/${userId}/approved` })
      .then((res: any) => {
        const stores = res.data?.stores || [];
        const match = stores.find((s: any) => s.id === bookingId);
        if (match) {
          setStoreDetails({
            storeName: match.storeName || '',
            logoId: match.logoId || null,
            logoUrl: match.logoUrl || null,
            activationStatus: match.activationStatus || 'Inactive',
            autoActivate: !!match.autoActivate
          });
          setStoreNameInput(match.storeName || '');
        }
      })
      .catch(() => {});
  };

  const loadAll = () => {
    if (!bookingId) return;
    setLoading(true);
    Promise.all([getRacks(), getAllProducts(), getInventory(), getStoreDetails(), getCooldown(), getMainStoreImportOptions(true)]).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  // ── Reservation cooldown actions ────────────────────────────────────
  const getCooldown = () => {
    setCooldownMinutes(null);
    return http.get({ url: `localstore/${bookingId}/reservation-cooldown` })
      .then((res: any) => {
        const minutes = Number(res.data?.minutes);
        setCooldownMinutes(HOLD_TIME_OPTIONS.includes(minutes) ? minutes : null);
      })
      .catch(() => {});
  };

  const saveCooldown = (minutes: number) => {
    setSavingCooldown(true);
    http.post({ url: `localstore/${bookingId}/reservation-cooldown`, data: { minutes } })
      .then((res: any) => {
        if (!res.data?.success) {
          AlertService.showMsg({ message: res.data?.error || "Could not save" });
          return;
        }
        setCooldownMinutes(minutes);
        AlertService.showSuccessMsg("Reservation hold time updated");
      })
      .catch(() => AlertService.showMsg({ message: "Could not save" }))
      .finally(() => setSavingCooldown(false));
  };

  // ── Store Details actions ───────────────────────────────────────────
  const saveStoreName = () => {
    if (!storeNameInput.trim()) {
      AlertService.showMsg({ message: "Please enter a store name" });
      return;
    }
    setSavingStoreName(true);
    http.post({ url: `localstore/${bookingId}/store-name`, data: { storeName: storeNameInput.trim() } })
      .then((res: any) => {
        if (!res.data?.success) {
          AlertService.showMsg({ message: res.data?.error || "Could not save store name" });
          return;
        }
        AlertService.showSuccessMsg("Store name updated");
        setStoreDetails(prev => ({ ...prev, storeName: storeNameInput.trim() }));
      })
      .catch(() => AlertService.showMsg({ message: "Could not save store name" }))
      .finally(() => setSavingStoreName(false));
  };

  const saveAutoActivate = (enabled: boolean) => {
    setSavingAutoActivate(true);
    http.post({ url: `localstore/${bookingId}/auto-activate`, data: { enabled } })
      .then((res: any) => {
        if (!res.data?.success) {
          AlertService.showMsg({ message: res.data?.error || "Could not save" });
          return;
        }
        setStoreDetails(prev => ({ ...prev, autoActivate: enabled }));
        AlertService.showSuccessMsg(enabled ? "Auto-activation turned on" : "Auto-activation turned off");
      })
      .catch(() => AlertService.showMsg({ message: "Could not save" }))
      .finally(() => setSavingAutoActivate(false));
  };

  const assignLogo = (logoId: number | null) => {
    http.post({ url: `localstore/${bookingId}/assign-logo`, data: { logoId } })
      .then((res: any) => {
        if (!res.data?.success) {
          AlertService.showMsg({ message: res.data?.error || "Could not update logo" });
          return;
        }
        AlertService.showSuccessMsg(logoId ? "Logo assigned" : "Logo removed from stall");
        const matched = logos.find(l => l.id === logoId);
        setStoreDetails(prev => ({ ...prev, logoId, logoUrl: matched ? matched.imageUrl : null }));
        setLogoPickerOpen(false);
      })
      .catch(() => AlertService.showMsg({ message: "Could not update logo" }));
  };

  // ── Logo library ─────────────────────────────────────────────────
  const getLogos = () => {
    if (!userId) return;
    setLoadingLogos(true);
    http.get({ url: `localstore/user/${userId}/logos` })
      .then((res: any) => setLogos(res.data?.logos || []))
      .catch(() => setLogos([]))
      .finally(() => setLoadingLogos(false));
  };

  useEffect(() => {
    if (logoPickerOpen) getLogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoPickerOpen]);

  const handleLogoUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    setUploadingLogo(true);
    try {
      const presignRes: any = await http.post({
        url: `localstore/logos/presign-image`,
        data: { fileName: file.name, contentType: file.type }
      });

      if (!presignRes.data?.success) {
        throw new Error(presignRes.data?.error || 'Could not get upload URL');
      }

      const slot: PresignSlot = presignRes.data;

      await fetch(slot.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      const saveRes: any = await http.post({
        url: `localstore/logos`,
        data: { imageUrl: slot.publicUrl, key: slot.key }
      });

      if (!saveRes.data?.success) {
        throw new Error(saveRes.data?.error || 'Could not save logo');
      }

      AlertService.showSuccessMsg("Logo added to your library");
      getLogos();
    } catch (err: any) {
      AlertService.showMsg({ message: err?.message || 'Could not upload logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = (logoId: number) => {
    http.post({ url: `localstore/logo/${logoId}/remove`, data: null })
      .then((res: any) => {
        if (!res.data?.success) {
          AlertService.showMsg({ message: res.data?.error || "Could not remove logo" });
          return;
        }
        AlertService.showSuccessMsg("Logo removed");
        getLogos();
      })
      .catch(() => AlertService.showMsg({ message: "Could not remove logo" }));
  };

  // ── Image upload (presigned S3) — products ──────────────────────────
  const uploadProductImages = async (files: File[]): Promise<string[]> => {
    if (files.length === 0) return [];

    const presignRes: any = await http.post({
      url: `localstore/products/presign-images`,
      data: { files: files.map(f => ({ fileName: f.name, contentType: f.type })) }
    });

    if (!presignRes.data?.success) {
      throw new Error(presignRes.data?.error || 'Could not get upload URLs');
    }

    const slots: PresignSlot[] = presignRes.data.slots;

    await Promise.all(
      slots.map((slot, i) =>
        fetch(slot.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': files[i].type },
          body: files[i]
        })
      )
    );

    return slots.map(s => s.publicUrl);
  };

  // ── Add/Edit Product ────────────────────────────────────────────
  const openAddProduct = () => {
    setEditingProductId(null);
    setProductName('');
    setProductQuantity('');
    setProductPrice('');
    setProductDescription('');
    setExistingPhotos([]);
    setNewPhotoFiles([]);
    setProductDialogOpen(true);
  };

  const openEditProduct = (product: ProductOption) => {
    setEditingProductId(product.id);
    setProductName(product.name);
    setProductQuantity(String(product.quantity));
    setProductPrice(product.price != null ? String(product.price) : '');
    setProductDescription(product.description || '');
    setExistingPhotos(product.photos || []);
    setNewPhotoFiles([]);
    setProductDialogOpen(true);
  };

  const closeProductDialog = () => setProductDialogOpen(false);

  const totalPhotoCount = existingPhotos.length + newPhotoFiles.length;

  const handleFilePick = (files: FileList | null) => {
    if (!files) return;
    const picked = Array.from(files);
    if (totalPhotoCount + picked.length > 3) {
      AlertService.showMsg({ message: 'Maximum 3 images per product' });
      return;
    }
    setNewPhotoFiles(prev => [...prev, ...picked]);
  };

  const removeExistingPhoto = (url: string) => setExistingPhotos(prev => prev.filter(p => p !== url));
  const removeNewPhoto = (idx: number) => setNewPhotoFiles(prev => prev.filter((_, i) => i !== idx));

  const refreshAll = () => {
    getAllProducts();
    getInventory();
  };

  // ── Import from main store ──────────────────────────────────────────
  // silent=true is used for the page-load fetch (button-visibility check) so
  // it doesn't flash a spinner inside a dialog that isn't even open yet.
  const getMainStoreImportOptions = (silent = false) => {
    if (!silent) setLoadingMainStoreProducts(true);
    return http.get({ url: `localstore/${bookingId}/main-store-products` })
      .then((res: any) => {
        if (!res.data?.success) {
          if (!silent) AlertService.showMsg({ message: res.data?.error || "Could not load your main store products" });
          setMainStoreStalls([]);
          setMainStoreProducts([]);
          return;
        }
        const stalls: { id: number; name: string }[] = res.data?.stalls || [];
        const products: MainStoreProduct[] = res.data?.products || [];
        setMainStoreStalls(stalls);
        setMainStoreProducts(products);
        // Every stall starts selected, and every not-yet-imported product
        // starts checked — the merchant unchecks what they don't want.
        setSelectedStallIds(stalls.map(s => s.id));
        setCheckedStallItemIds(new Set(products.filter(p => !p.alreadyImported).map(p => p.stallItemId)));
      })
      .catch(() => {
        if (!silent) AlertService.showMsg({ message: "Could not load your main store products" });
        setMainStoreStalls([]);
        setMainStoreProducts([]);
      })
      .finally(() => setLoadingMainStoreProducts(false));
  };

  const openImportDialog = () => {
    setImportDialogOpen(true);
    getMainStoreImportOptions();
  };

  const closeImportDialog = () => setImportDialogOpen(false);

  const stallOptions = React.useMemo(() => {
    const byId = new Map<number, string>();
    mainStoreProducts.forEach(p => {
      if (!byId.has(p.stallId)) byId.set(p.stallId, p.stallName || `Stall #${p.stallId}`);
    });
    return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
  }, [mainStoreProducts]);

  const visibleMainStoreProducts = mainStoreProducts.filter(p => selectedStallIds.includes(p.stallId));
  const visibleCheckedCount = visibleMainStoreProducts.filter(p => !p.alreadyImported && checkedStallItemIds.has(p.stallItemId)).length;

  const toggleStallSelected = (stallId: number) => {
    setSelectedStallIds(prev => prev.includes(stallId) ? prev.filter(id => id !== stallId) : [...prev, stallId]);
  };

  const toggleStallItemChecked = (stallItemId: number) => {
    setCheckedStallItemIds(prev => {
      const next = new Set(prev);
      if (next.has(stallItemId)) next.delete(stallItemId); else next.add(stallItemId);
      return next;
    });
  };

  const handleImportSelected = async () => {
    const selected = visibleMainStoreProducts.filter(p => !p.alreadyImported && checkedStallItemIds.has(p.stallItemId));
    if (selected.length === 0) {
      AlertService.showMsg({ message: "Select at least one product to import" });
      return;
    }

    setImportingProducts(true);
    try {
      const items = selected.map(p => ({
        stallItemId: p.stallItemId,
        name: p.name,
        description: p.description,
        quantity: p.quantity,
        price: p.price,
        // Main-store images are relative paths everywhere else in this app —
        // resolved to a full URL here so it's saved ready-to-display, same
        // as every other LocalStore product photo.
        photos: p.imagePath ? [general.getImageUrl(p.imagePath)] : []
      }));

      const res: any = await http.post({ url: `localstore/${bookingId}/products/import-batch`, data: { items } });
      if (!res.data?.success) {
        AlertService.showMsg({ message: res.data?.error || "Could not import products" });
        return;
      }
      const { importedCount, skippedCount } = res.data;
      AlertService.showSuccessMsg(
        skippedCount > 0
          ? `${importedCount} product${importedCount !== 1 ? 's' : ''} imported (${skippedCount} already in your inventory)`
          : `${importedCount} product${importedCount !== 1 ? 's' : ''} imported to inventory`
      );
      setImportDialogOpen(false);
      refreshAll();
      getMainStoreImportOptions(true); // refresh alreadyImported flags for next time
    } catch {
      AlertService.showMsg({ message: "Could not import products" });
    } finally {
      setImportingProducts(false);
    }
  };

  const handleSaveProduct = async () => {
    if (!productName.trim()) {
      AlertService.showMsg({ message: "Product name is required" });
      return;
    }
    const qty = parseInt(productQuantity, 10);
    if (isNaN(qty) || qty < 0) {
      AlertService.showMsg({ message: "Please enter a valid quantity" });
      return;
    }
    const price = productPrice.trim() === '' ? null : parseFloat(productPrice);
    if (price !== null && (isNaN(price) || price < 0)) {
      AlertService.showMsg({ message: "Please enter a valid price, or leave it blank" });
      return;
    }
    if (totalPhotoCount < 1) {
      AlertService.showMsg({ message: "Please add at least 1 product photo" });
      return;
    }
    if (totalPhotoCount > 3) {
      AlertService.showMsg({ message: "Maximum 3 photos per product" });
      return;
    }

    setSavingProduct(true);
    try {
      const uploadedUrls = newPhotoFiles.length > 0 ? await uploadProductImages(newPhotoFiles) : [];
      const photos = [...existingPhotos, ...uploadedUrls];

      const payload: any = {
        name: productName.trim(),
        quantity: qty,
        price: price,
        description: productDescription.trim() || null,
        photos
      };

      let request;
      if (editingProductId) {
        payload.requestingBookingId = bookingId;
        request = http.post({ url: `localstore/product/${editingProductId}/update`, data: payload });
      } else {
        request = http.post({ url: `localstore/${bookingId}/products`, data: payload });
      }

      const res: any = await request;
      if (!res.data?.success) {
        AlertService.showMsg({ message: res.data?.error || 'Could not save product' });
        return;
      }

      AlertService.showSuccessMsg(editingProductId ? "Product updated" : "Product added to inventory");
      setProductDialogOpen(false);
      refreshAll();
    } catch (err: any) {
      AlertService.showMsg({ message: err?.message || 'Could not save product' });
    } finally {
      setSavingProduct(false);
    }
  };

  const handleRemoveProduct = (productId: number) => {
    http.post({ url: `localstore/product/${productId}/remove`, data: { requestingBookingId: bookingId } })
      .then((res: any) => {
        if (!res.data?.success) {
          AlertService.showMsg({ message: res.data?.error || "Could not remove product" });
          return;
        }
        AlertService.showSuccessMsg("Product removed");
        refreshAll();
      })
      .catch(() => AlertService.showMsg({ message: "Could not remove product" }));
  };

  const handleUnassignProduct = (productId: number) => {
    http.post({ url: `localstore/product/${productId}/unassign-rack`, data: { requestingBookingId: bookingId } })
      .then((res: any) => {
        if (!res.data?.success) {
          AlertService.showMsg({ message: res.data?.error || "Could not move product" });
          return;
        }
        AlertService.showSuccessMsg("Moved back to inventory");
        refreshAll();
      })
      .catch(() => AlertService.showMsg({ message: "Could not move product" }));
  };

  // ── Big "Add Product to Rack" modal ───────────────────────────────
  const openAssignDialog = (rack: RackOption) => {
    setAssignTargetRackId(rack.id);
    setAssignTargetRackLabel(rack.label);
    setAssignSearchText('');
    setAssignSort('none');
    setAssignDialogOpen(true);
  };

  const closeAssignDialog = () => setAssignDialogOpen(false);

  const searchInventory = () => {
    setLoadingAssignResults(true);
    http.get({
      url: `localstore/${bookingId}/inventory/search?q=${encodeURIComponent(assignSearchText)}&sort=${encodeURIComponent(assignSort)}`
    })
      .then((res: any) => setAssignResults(res.data?.products || []))
      .catch(() => setAssignResults([]))
      .finally(() => setLoadingAssignResults(false));
  };

  useEffect(() => {
    if (assignDialogOpen) searchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignDialogOpen, assignSearchText, assignSort]);

  const handleAssign = (productId: number) => {
    http.post({
      url: `localstore/product/${productId}/assign-rack`,
      data: { rackId: assignTargetRackId, requestingBookingId: bookingId }
    })
      .then((res: any) => {
        if (!res.data?.success) {
          AlertService.showMsg({ message: res.data?.error || "Could not assign product" });
          return;
        }
        AlertService.showSuccessMsg("Product placed on rack");
        searchInventory();
        refreshAll();
      })
      .catch(() => AlertService.showMsg({ message: "Could not assign product" }));
  };

  // ── Fulfilment toggles ────────────────────────────────────────────
  const handleFulfilmentChange = (field: 'pickup' | 'delivery', value: boolean) => {
    const nextPickup = field === 'pickup' ? value : isPickupEnabled;
    const nextDelivery = field === 'delivery' ? value : isDeliveryEnabled;

    setIsPickupEnabled(nextPickup);
    setIsDeliveryEnabled(nextDelivery);

    http.post({ url: `localstore/${bookingId}/fulfilment`, data: { isPickupEnabled: nextPickup, isDeliveryEnabled: nextDelivery } })
      .catch(() => AlertService.showMsg({ message: "Could not save fulfilment settings" }));
  };

  // Racks come back from the backend in whatever order they were created —
  // often alphabetical ("Front", "Last", "Mid"), which reads wrong physically.
  // Sort by the shelf's natural front-to-back position instead; anything not
  // matching a known label keeps its original relative order, after these.
  const RACK_POSITION_ORDER = ['front', 'mid', 'middle', 'last', 'back'];
  const rackPositionRank = (label: string) => {
    const normalized = label.trim().toLowerCase();
    const idx = RACK_POSITION_ORDER.findIndex(keyword => normalized.includes(keyword));
    return idx === -1 ? RACK_POSITION_ORDER.length : idx;
  };
  const sortedRacks = [...racks].sort((a, b) => rackPositionRank(a.label) - rackPositionRank(b.label));

  const productsByRack = sortedRacks.map(rack => ({
    rack,
    products: allProducts.filter(p => p.rackId === rack.id)
  }));

  const totalPlacedCount = allProducts.filter(p => p.rackId !== null).length;

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" className="ls-loading-screen">
        <CircularProgress size={26} thickness={4} />
        <Typography className="ls-loading-text">Loading your store…</Typography>
      </Box>
    );
  }

  return (
    <div className="localstore-inventory-ui">
      <div className="ls-header">
        <button className="ls-back-btn" onClick={() => back()} aria-label="Go back">
          <ArrowBackIcon fontSize="small" />
        </button>
        <div className="ls-header-text">
          <span className="ls-eyebrow">Manage store</span>
          <Typography className="ls-title">
            {storeDetails.storeName || 'Store details & inventory'}
          </Typography>
        </div>
        {isLocked && (
          <div className="ls-locked-pill">
            <LockOutlinedIcon fontSize="inherit" />
            Live — locked
          </div>
        )}
      </div>

      <div className="ls-tabs-wrap">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className="ls-tabs"
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ className: 'ls-tab-indicator' }}
        >
          <Tab disableRipple label={<span className="ls-tab-label"><StorefrontIcon fontSize="small" />Store Details</span>} />
          <Tab
            disableRipple
            label={<span className="ls-tab-label"><LocalMallIcon fontSize="small" />Inventory{inventory.length > 0 && <span className="ls-tab-count">{inventory.length}</span>}</span>}
          />
          <Tab
            disableRipple
            label={<span className="ls-tab-label"><WidgetsIcon fontSize="small" />Racks{racks.length > 0 && <span className="ls-tab-count">{racks.length}</span>}</span>}
          />
        </Tabs>
        <button className="ls-breadcrumb-back" onClick={() => back()}>
          <ArrowBackIcon fontSize="inherit" />
          Back to My Stalls
        </button>
      </div>

      {/* ── Validation error banner ──────────────────────────────── */}
      {storeDetailsError && activeTab === 0 && (
        <div className="ls-validation-error">
          <span className="ls-validation-error-icon">!</span>
          <span>{storeDetailsError}</span>
        </div>
      )}

      {/* ── STORE DETAILS TAB (now includes logo management inline) ──── */}
      {activeTab === 0 && (
        <div className="ls-content">
          {isLocked && (
            <div className="ls-locked-banner">
              <LockOutlinedIcon fontSize="small" />
              <span>This stall is live, so its name and logo are locked. They'll unlock again for your next booking.</span>
            </div>
          )}

          <div className="ls-store-details-card">
            <div className="ls-store-logo-block">
              <div className="ls-store-logo-preview">
                {storeDetails.logoUrl ? (
                  <img src={storeDetails.logoUrl} alt="Store logo" />
                ) : (
                  <div className="ls-store-logo-placeholder"><ImageIcon /></div>
                )}
              </div>
              <Button
                size="small"
                className="ls-add-btn"
                startIcon={<EditIcon fontSize="small" />}
                onClick={() => setLogoPickerOpen(true)}
                disabled={isLocked}
              >
                {storeDetails.logoUrl ? 'Change logo' : 'Add logo'}
              </Button>
            </div>

            <div className="ls-store-name-block">
              <Typography className="ls-section-label" style={{ marginBottom: 8 }}>Store name</Typography>
              <TextField
                className="ls-field"
                placeholder="e.g. Priya's Pickles"
                variant="outlined"
                size="small"
                fullWidth
                value={storeNameInput}
                onChange={(e) => setStoreNameInput(e.target.value)}
                disabled={isLocked}
              />
              <Typography className="ls-hint-text" style={{ marginBottom: 14 }}>
                Both a name and a logo are required before you can activate this stall.
              </Typography>
              <Button
                size="small"
                className="ls-add-btn ls-add-btn-solid"
                onClick={saveStoreName}
                disabled={isLocked || savingStoreName || storeNameInput.trim() === (storeDetails.storeName || '')}
              >
                {savingStoreName ? 'Saving…' : 'Save name'}
              </Button>
            </div>
          </div>

          <div className="ls-store-details-card" style={{ marginTop: 16 }}>
            <Typography id="ls-hold-time-label" className="ls-section-label" style={{ marginBottom: 8 }}>Reservation hold time <span className="ls-required">*</span></Typography>
            <Typography className="ls-hint-text" style={{ marginBottom: 14 }}>
              How long an order (and the stock it reserved) stays held for a customer before it's automatically released back for others to order.
            </Typography>
            <div className="ls-hold-time-options" role="radiogroup" aria-labelledby="ls-hold-time-label" aria-required="true">
              {HOLD_TIME_OPTIONS.map(mins => (
                <label key={mins} className="ls-hold-time-option">
                  <input
                    type="radio"
                    name="reservationHoldTime"
                    value={mins}
                    checked={cooldownMinutes === mins}
                    onChange={() => saveCooldown(mins)}
                    disabled={savingCooldown}
                    required
                  />
                  <span>{mins} min</span>
                </label>
              ))}
            </div>
            <Typography className="ls-hint-text" style={{ marginTop: 10 }} aria-live="polite">
              {savingCooldown ? 'Saving reservation hold time…' : cooldownMinutes === null
                ? 'Select a reservation hold time to continue (required).'
                : `Selected: ${cooldownMinutes} minutes`}
            </Typography>
          </div>
        </div>
      )}

      {/* ── INVENTORY TAB ─────────────────────────────────────────── */}
      {activeTab === 1 && (
        <div className="ls-content">
          <div className="ls-section-header-row">
            <div>
              <Typography className="ls-section-title">Inventory</Typography>
              <Typography className="ls-hint-text">
                Shared across every stall at this community. New products land here first — place them on a rack from the Racks tab.
              </Typography>
            </div>
            <div className="ls-inventory-header-actions">
              {mainStoreStalls.length > 0 && (
                <Button
                  size="small"
                  className="ls-add-btn"
                  startIcon={<StoreOutlinedIcon fontSize="small" />}
                  onClick={openImportDialog}
                >
                  Import from my main store
                </Button>
              )}
              <Button
                size="small"
                className="ls-add-btn ls-add-btn-solid"
                startIcon={<AddIcon fontSize="small" />}
                onClick={openAddProduct}
              >
                Add product
              </Button>
            </div>
          </div>

          {inventory.length === 0 && (
            <div className="ls-empty-state">
              <div className="ls-empty-icon-wrap"><LocalMallIcon /></div>
              <Typography className="ls-empty-title">No unassigned products</Typography>
              <Typography className="ls-empty-sub">Products you add will wait here until you place them on a rack.</Typography>
            </div>
          )}

          <Grid container spacing={2}>
            {inventory.map((product) => (
              <Grid item xs={6} sm={4} md={3} key={product.id}>
                <div className="ls-inventory-card">
                  <div className="ls-inventory-thumb-wrap">
                    {product.photos && product.photos[0] ? (
                      <img src={product.photos[0]} alt={product.name} className="ls-inventory-thumb" />
                    ) : (
                      <div className="ls-inventory-thumb-placeholder"><ImageIcon /></div>
                    )}
                    <IconButton size="small" className="ls-inventory-remove" onClick={() => handleRemoveProduct(product.id)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </div>
                  <div className="ls-inventory-body" onClick={() => openEditProduct(product)}>
                    <div className="ls-inventory-name">{product.name}</div>
                    <div className="ls-inventory-chips">
                      <span className="ls-chip ls-chip-qty">Qty {product.quantity}</span>
                      {product.price != null
                        ? <span className="ls-chip ls-chip-price">₹{product.price}</span>
                        : <span className="ls-chip ls-chip-neutral">Ask price</span>}
                    </div>
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      )}

      {/* ── RACKS TAB ─────────────────────────────────────────────── */}
      {activeTab === 2 && (
        <div className="ls-content">
          <div className="ls-fulfil-card">
            <div className="ls-fulfil-heading">
              <StorefrontIcon fontSize="small" />
              <Typography className="ls-fulfil-title">Fulfilment options</Typography>
            </div>
            <Typography className="ls-fulfil-sub">Choose how residents can receive orders from this stall.</Typography>

            <div className="ls-fulfil-row">
              <div className="ls-fulfil-option-label">
                <DirectionsWalkIcon fontSize="small" />
                <div>
                  <div className="ls-fulfil-option-name">Pickup</div>
                  <div className="ls-fulfil-option-desc">Resident collects the order at your stall</div>
                </div>
              </div>
              <Switch
                checked={isPickupEnabled}
                onChange={(e) => handleFulfilmentChange('pickup', e.target.checked)}
                color="primary"
              />
            </div>

            <div className="ls-fulfil-row">
              <div className="ls-fulfil-option-label">
                <LocalShippingOutlinedIcon fontSize="small" />
                <div>
                  <div className="ls-fulfil-option-name">Delivery</div>
                  <div className="ls-fulfil-option-desc">You drop the order at the resident's door</div>
                </div>
              </div>
              <Switch
                checked={isDeliveryEnabled}
                onChange={(e) => handleFulfilmentChange('delivery', e.target.checked)}
                color="primary"
              />
            </div>

            <div className={`ls-fulfil-row${isLocked ? ' ls-fulfil-row-disabled' : ''}`}>
              <div className="ls-fulfil-option-label">
                <CheckCircleIcon fontSize="small" />
                <div>
                  <div className="ls-fulfil-option-name">Activate stall automatically</div>
                  <div className="ls-fulfil-option-desc">
                    {isLocked
                      ? "Your stall is already live for this booking — Auto Activate is disabled"
                      : "Skip the QR scan — your stall goes live on its own once your booked slot starts (name and logo still need to be set)"}
                  </div>
                </div>
              </div>
              <Switch
                checked={storeDetails.autoActivate}
                disabled={savingAutoActivate || isLocked}
                onChange={(e) => saveAutoActivate(e.target.checked)}
                color="primary"
              />
            </div>
          </div>

          {racks.length > 0 && (
            <div className="ls-section-label">
              <span>{racks.length} rack{racks.length !== 1 ? 's' : ''}</span>
              <span className="ls-section-label-dot">·</span>
              <span>{totalPlacedCount} product{totalPlacedCount !== 1 ? 's' : ''} on display</span>
            </div>
          )}

          {racks.length === 0 && (
            <div className="ls-empty-state">
              <div className="ls-empty-icon-wrap"><WidgetsIcon /></div>
              <Typography className="ls-empty-title">No racks set up yet</Typography>
              <Typography className="ls-empty-sub">Your community admin sets up racks for your stall — check back once they've been added.</Typography>
            </div>
          )}

          {productsByRack.map(({ rack, products: rackProducts }) => (
            <div className="ls-rack-card" key={rack.id}>
              <div className="ls-rack-header">
                <div className="ls-rack-title-group">
                  <div className="ls-rack-icon-badge"><WidgetsIcon fontSize="small" /></div>
                  <div>
                    <Typography className="ls-rack-title">{rack.label}</Typography>
                    {rack.locationNote && <div className="ls-rack-note">{rack.locationNote}</div>}
                  </div>
                </div>
                <Button
                  size="small"
                  className="ls-add-btn"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => openAssignDialog(rack)}
                >
                  Place product
                </Button>
              </div>

              {rackProducts.length === 0 && (
                <div className="ls-rack-empty">Nothing placed here yet</div>
              )}

              {rackProducts.map((product) => (
                <div className="ls-rack-product-row" key={product.id}>
                  <div className="ls-rack-product-info">
                    {product.photos && product.photos[0] ? (
                      <img src={product.photos[0]} alt={product.name} className="ls-rack-product-thumb" />
                    ) : (
                      <div className="ls-rack-product-thumb-placeholder"><ImageIcon fontSize="small" /></div>
                    )}
                    <div className="ls-rack-product-text">
                      <div className="ls-rack-product-name" onClick={() => openEditProduct(product)}>
                        {product.name}
                        {product.source === 'Imported' && <span className="ls-imported-tag">imported</span>}
                      </div>
                      <div className="ls-rack-product-meta">
                        Qty {product.quantity} {product.price != null ? `· ₹${product.price}` : '· price on request'}
                      </div>
                    </div>
                  </div>
                   <div className="ls-rack-product-actions">
                    <IconButton
                      size="small"
                      className="ls-icon-danger ls-icon-danger-solid"
                      onClick={() => handleUnassignProduct(product.id)}
                      title="Remove from rack"
                      aria-label="Remove from rack"
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── Previous / Next — lets a merchant step through the tabs
           in order instead of having to hunt for the right one. ──── */}
      <div className="ls-tab-nav-shell">
        <div className="ls-tab-nav">
          <button
            type="button"
            className="ls-tab-nav-btn ls-tab-nav-btn-prev"
            onClick={goToPreviousTab}
            disabled={activeTab === 0}
          >
            <ArrowBackIcon fontSize="small" /> Previous
          </button>
          <span className="ls-tab-nav-position">Step {activeTab + 1} of {TAB_COUNT}</span>
          <button
            type="button"
            className="ls-tab-nav-btn ls-tab-nav-btn-next"
            onClick={goToNextTab}
            disabled={activeTab === TAB_COUNT - 1 && totalPlacedCount === 0}
          >
            Next <ArrowForwardIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
           Update Success Dialog
           ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={updateSuccessDialogOpen}
        onClose={() => setUpdateSuccessDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: 'lsi-dialog-paper' }}
      >
        <DialogTitle className="lsi-dialog-title" disableTypography>
          <div className="lsi-dialog-title-group">
            <div className="lsi-dialog-title-icon"><CheckCircleIcon style={{ fontSize: 18 }} /></div>
            <span className="lsi-dialog-title-text">Update complete</span>
          </div>
          <IconButton size="small" className="lsi-dialog-close-btn" onClick={() => setUpdateSuccessDialogOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="lsi-dialog-content">
          <Typography>Local store details updated successfully.</Typography>
        </DialogContent>
        <DialogActions className="lsi-dialog-actions">
          <Button className="lsi-btn-solid" onClick={() => setUpdateSuccessDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Logo Picker Dialog */}
      <Dialog
        open={logoPickerOpen}
        onClose={() => setLogoPickerOpen(false)}
        maxWidth="sm"
        fullWidth
        classes={{ paper: 'lsi-dialog-paper' }}
      >
        <DialogTitle className="lsi-dialog-title" disableTypography>
          <span className="lsi-dialog-title-text">Choose or upload a logo</span>
          <IconButton size="small" className="lsi-dialog-close-btn" onClick={() => setLogoPickerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="lsi-dialog-content">
          {/* Upload area */}
          <label className="lsi-logo-upload-zone">
            <div className="lsi-logo-upload-zone-inner">
              {uploadingLogo
                ? <CircularProgress size={20} thickness={5} className="lsi-btn-spinner" />
                : <UploadIcon style={{ fontSize: 28 }} />}
              <span className="lsi-logo-upload-zone-title">
                {uploadingLogo ? 'Uploading…' : 'Upload a new logo'}
              </span>
              <span className="lsi-logo-upload-zone-hint">
                PNG, JPG or SVG · recommended 512×512
              </span>
            </div>
            <input type="file" accept="image/*" hidden disabled={uploadingLogo} onChange={(e) => handleLogoUpload(e.target.files)} />
          </label>

          {loadingLogos && (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={22} thickness={4} /></Box>
          )}

          {!loadingLogos && logos.length > 0 && (
            <>
              <div className="lsi-logo-section-divider">
                <span>or pick from your library</span>
              </div>
              <div className="lsi-logo-grid">
                {logos.map((logo) => (
                  <div
                    key={logo.id}
                    className={`lsi-logo-tile${storeDetails.logoId === logo.id ? ' lsi-logo-tile-selected' : ''}`}
                    onClick={() => assignLogo(logo.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <img src={logo.imageUrl} alt="Logo" />
                    {storeDetails.logoId === logo.id && (
                      <span className="lsi-logo-tile-check"><CheckCircleIcon fontSize="small" /></span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions className="lsi-dialog-actions">
          {storeDetails.logoId && (
            <Button className="lsi-btn-ghost lsi-btn-danger-text" onClick={() => assignLogo(null)}>Remove logo</Button>
          )}
          <Button className="lsi-btn-ghost" onClick={() => setLogoPickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════
           Add / Edit Product Dialog
           ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={productDialogOpen}
        onClose={closeProductDialog}
        maxWidth="xs"
        fullWidth
        classes={{ paper: 'lsi-dialog-paper' }}
      >
        <DialogTitle className="lsi-dialog-title" disableTypography>
          <span className="lsi-dialog-title-text">{editingProductId ? 'Edit product' : 'Add product to inventory'}</span>
          <IconButton size="small" className="lsi-dialog-close-btn" onClick={closeProductDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="lsi-dialog-content">
          {/* ─ Photos section ─ */}
          <div className="lsi-product-section">
            <div className="lsi-product-section-header">
              <div className="lsi-product-section-icon"><CollectionsIcon style={{ fontSize: 16 }} /></div>
              <Typography className="lsi-product-section-title">Product photos</Typography>
              <span className={`lsi-photo-count${totalPhotoCount < 1 ? ' lsi-photo-count-warn' : ''}`}>
                {totalPhotoCount}/3
              </span>
            </div>
            <div className="lsi-photo-grid">
              {existingPhotos.map(url => (
                <div key={url} className="lsi-photo-thumb-wrap">
                  <img src={url} alt="" className="lsi-photo-thumb" />
                  <IconButton size="small" className="lsi-photo-remove" onClick={() => removeExistingPhoto(url)}>
                    <CloseIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </div>
              ))}
              {newPhotoFiles.map((f, i) => (
                <div key={i} className="lsi-photo-thumb-wrap">
                  <img src={URL.createObjectURL(f)} alt="" className="lsi-photo-thumb" />
                  <IconButton size="small" className="lsi-photo-remove" onClick={() => removeNewPhoto(i)}>
                    <CloseIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </div>
              ))}
              {totalPhotoCount < 3 && (
                <label className="lsi-photo-upload-btn">
                  <UploadIcon style={{ fontSize: 20 }} />
                  <span>Upload</span>
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => handleFilePick(e.target.files)} />
                </label>
              )}
            </div>
            <Typography className="lsi-photo-hint">
              At least 1 photo required · max 3
            </Typography>
          </div>

          {/* ─ Details section ─ */}
          <div className="lsi-product-section">
            <div className="lsi-product-section-header">
              <div className="lsi-product-section-icon"><LocalMallIcon style={{ fontSize: 16 }} /></div>
              <Typography className="lsi-product-section-title">Details</Typography>
            </div>
            <TextField
              className="lsi-field"
              label="Product name"
              variant="outlined"
              size="small"
              fullWidth
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
            <div className="lsi-field-row">
              <TextField
                className="lsi-field lsi-field-half"
                label="Quantity"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                value={productQuantity}
                onChange={(e) => setProductQuantity(e.target.value)}
              />
              <TextField
                className="lsi-field lsi-field-half"
                label="Price (₹)"
                placeholder="Optional"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                value={productPrice}
                onChange={(e) => setProductPrice(e.target.value)}
              />
            </div>
            <Typography className="lsi-field-caption">Leave price blank to mark it "reserve &amp; negotiate"</Typography>
            <TextField
              className="lsi-field"
              label="Description"
              placeholder="Optional — tell buyers more about this item"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
            />
          </div>
        </DialogContent>
        <DialogActions className="lsi-dialog-actions">
          <Button className="lsi-btn-ghost" onClick={closeProductDialog}>Cancel</Button>
          <Button className="lsi-btn-solid" onClick={handleSaveProduct} disabled={savingProduct}>
            {savingProduct
              ? <CircularProgress size={16} thickness={5} className="lsi-btn-spinner" />
              : (editingProductId ? 'Save changes' : 'Add to inventory')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════
           Assign Product to Rack Dialog
           ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={assignDialogOpen}
        onClose={closeAssignDialog}
        maxWidth="sm"
        fullWidth
        classes={{ paper: 'lsi-dialog-paper' }}
      >
        <DialogTitle className="lsi-dialog-title" disableTypography>
          <div className="lsi-dialog-title-group">
            <div className="lsi-dialog-title-icon"><WidgetsIcon style={{ fontSize: 18 }} /></div>
            <div>
              <span className="lsi-dialog-title-text">Place product on rack</span>
              <span className="lsi-dialog-subtitle">{assignTargetRackLabel}</span>
            </div>
          </div>
          <IconButton size="small" className="lsi-dialog-close-btn" onClick={closeAssignDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="lsi-dialog-content lsi-assign-dialog-content">
          <div className="lsi-assign-toolbar">
            <TextField
              className="lsi-search-field"
              placeholder="Search inventory…"
              variant="outlined"
              size="small"
              value={assignSearchText}
              onChange={(e) => setAssignSearchText(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" className="lsi-search-icon" /> }}
            />
            <FormControl className="lsi-sort-field" variant="outlined" size="small">
              <InputLabel>Sort</InputLabel>
              <Select value={assignSort} onChange={(e) => setAssignSort(e.target.value as 'none' | 'newest')} label="Sort">
                <MenuItem value="none">No sorting</MenuItem>
                <MenuItem value="newest">Newest first</MenuItem>
              </Select>
            </FormControl>
          </div>

          {loadingAssignResults && (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress size={22} thickness={4} /></Box>
          )}

          {!loadingAssignResults && assignResults.length === 0 && (
            <div className="lsi-assign-empty">
              <div className="lsi-assign-empty-icon"><LocalMallIcon /></div>
              <Typography className="lsi-assign-empty-title">No matching products</Typography>
              <Typography className="lsi-assign-empty-sub">Try a different search, or add a new product to inventory first.</Typography>
            </div>
          )}

          {!loadingAssignResults && assignResults.length > 0 && (
            <div className="lsi-assign-list">
              {assignResults.map((product) => (
                <div className="lsi-assign-card" key={product.id}>
                  <div className="lsi-assign-card-img">
                    {product.photos && product.photos[0] ? (
                      <img src={product.photos[0]} alt={product.name} />
                    ) : (
                      <div className="lsi-assign-card-img-placeholder"><ImageIcon /></div>
                    )}
                  </div>
                  <div className="lsi-assign-card-body">
                    <Typography className="lsi-assign-card-name">{product.name}</Typography>
                    <div className="lsi-assign-card-meta">
                      <span className="lsi-assign-chip">Qty {product.quantity}</span>
                      {product.price != null
                        ? <span className="lsi-assign-chip lsi-assign-chip-price">₹{product.price}</span>
                        : <span className="lsi-assign-chip lsi-assign-chip-neutral">Ask price</span>}
                    </div>
                  </div>
                  <Button size="small" className="lsi-place-here-btn" onClick={() => handleAssign(product.id)}>
                    <AddIcon style={{ fontSize: 16 }} /> Place
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions className="lsi-dialog-actions">
          <Button className="lsi-btn-ghost" onClick={closeAssignDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════
           Import from Main Store Dialog
           ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={importDialogOpen}
        onClose={closeImportDialog}
        maxWidth="sm"
        fullWidth
        classes={{ paper: 'lsi-dialog-paper' }}
      >
        <DialogTitle className="lsi-dialog-title" disableTypography>
          <div className="lsi-dialog-title-group">
            <div className="lsi-dialog-title-icon"><StoreOutlinedIcon style={{ fontSize: 18 }} /></div>
            <div>
              <span className="lsi-dialog-title-text">Import from my main store</span>
              <span className="lsi-dialog-subtitle">Uses each product's current name, photo, quantity and price</span>
            </div>
          </div>
          <IconButton size="small" className="lsi-dialog-close-btn" onClick={closeImportDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="lsi-dialog-content lsi-import-dialog-content">
          {loadingMainStoreProducts && (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress size={22} thickness={4} /></Box>
          )}

          {!loadingMainStoreProducts && mainStoreProducts.length === 0 && (
            <div className="lsi-assign-empty">
              <div className="lsi-assign-empty-icon"><StoreOutlinedIcon /></div>
              <Typography className="lsi-assign-empty-title">No products found</Typography>
              <Typography className="lsi-assign-empty-sub">None of your stalls have any products listed yet.</Typography>
            </div>
          )}

          {!loadingMainStoreProducts && mainStoreProducts.length > 0 && (
            <>
              {stallOptions.length > 1 && (
                <div className="lsi-import-stall-filter">
                  <Typography className="lsi-import-section-label">Stalls</Typography>
                  <div className="lsi-import-stall-chips">
                    {stallOptions.map(stall => (
                      <button
                        type="button"
                        key={stall.id}
                        className={`lsi-import-stall-chip${selectedStallIds.includes(stall.id) ? ' lsi-import-stall-chip-active' : ''}`}
                        onClick={() => toggleStallSelected(stall.id)}
                      >
                        {stall.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="lsi-import-list">
                {visibleMainStoreProducts.map(product => {
                  const checked = checkedStallItemIds.has(product.stallItemId);
                  return (
                    <label
                      key={product.stallItemId}
                      className={`lsi-import-row${product.alreadyImported ? ' lsi-import-row-disabled' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={product.alreadyImported ? false : checked}
                        disabled={product.alreadyImported}
                        onChange={() => toggleStallItemChecked(product.stallItemId)}
                      />
                      {product.imagePath ? (
                        <img src={general.getImageUrl(product.imagePath)} alt={product.name} className="lsi-import-row-img" />
                      ) : (
                        <div className="lsi-import-row-img-placeholder"><ImageIcon fontSize="small" /></div>
                      )}
                      <div className="lsi-import-row-body">
                        <Typography className="lsi-import-row-name">{product.name}</Typography>
                        <div className="lsi-import-row-meta">
                          <span>{stallOptions.length > 1 ? `${product.stallName} · ` : ''}Qty {product.quantity}</span>
                          {product.price != null
                            ? <span className="lsi-assign-chip lsi-assign-chip-price">₹{product.price}</span>
                            : <span className="lsi-assign-chip lsi-assign-chip-neutral">Ask price</span>}
                        </div>
                      </div>
                      {product.alreadyImported && <span className="lsi-import-row-tag">Already in inventory</span>}
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions className="lsi-dialog-actions">
          <Button className="lsi-btn-ghost" onClick={closeImportDialog}>Cancel</Button>
          <Button className="lsi-btn-solid" onClick={handleImportSelected} disabled={importingProducts || visibleCheckedCount === 0}>
            {importingProducts
              ? <CircularProgress size={16} thickness={5} className="lsi-btn-spinner" />
              : `Import selected (${visibleCheckedCount})`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LocalStoreInventory;
